'use client';

import { useEffect, useState } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { APP_ROUTES } from '@/shared/constants/navigation';
import { COPY } from '@/shared/constants/copies';
import { emitMixpanelEvent, resetMixpanel } from '@/shared/utils/mixpanel-utils';

import { useRouter } from '@shared/hooks/use-router';

import { cn } from '@/lib/utils';

import { REDIRECT_PATH } from '@/utils/constants';

import { Button } from '../ui/button';
import { IApiErrorProps } from './api-error.types';

function ApiError({ errorDetails, status, className }: IApiErrorProps) {
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const router = useRouter();

	const [countdown, setCountdown] = useState<number>(3);

	const displayCountdownText = errorDetails?.errorStatusCode === 401;

	useEffect(() => {
		if (pathname.includes('/forms/') && status === 401) {
			emitMixpanelEvent(mixpanelEvents.VIEWED_FORM_DASHBOARD_NO_ACCESS, {
				feature: MIXPANEL_APP_FEATURES.DASHBOARD,
			});
		}

		emitMixpanelEvent(mixpanelEvents.VIEWED_API_ERROR_SHOWN, {
			feature: MIXPANEL_APP_FEATURES.APP_LEVEL,
			error_status: status,
			error_message: errorDetails?.errorMessage,
			error_type: errorDetails?.errorType,
			is_message_from_backend: errorDetails?.isMessageFromBackend,
			is_cancelled_request: errorDetails?.isCancelledRequest,
			is_network_error: errorDetails?.isNetworkError,
		});

		if (!displayCountdownText) return;

		if (countdown === 1) {
			const newURLSearchParams = new URLSearchParams();
			newURLSearchParams.set(
				REDIRECT_PATH,
				encodeURIComponent(`${pathname}?${searchParams.toString()}`)
			);

			resetMixpanel();
			router.replace(`${APP_ROUTES.LOGOUT}?${searchParams.toString()}`);
			router.refresh();

			return;
		}

		const timer = setInterval(() => {
			setCountdown((prev) => {
				return prev - 1;
			});
		}, 1000);

		return () => {
			clearInterval(timer);
		};
	}, [
		displayCountdownText,
		countdown,
		router,
		searchParams,
		pathname,
		status,
		errorDetails?.errorMessage,
		errorDetails?.errorType,
		errorDetails?.isCancelledRequest,
		errorDetails?.isMessageFromBackend,
		errorDetails?.isNetworkError,
	]);

	if (status === 401) {
		return (
			<div className={cn('flex h-full flex-col items-center justify-center', className)}>
				<h1 className="mb-2 text-xl font-semibold text-gray-900">{COPY.access.restrictedTitle}</h1>
				<p className="text-center text-sm text-gray-600">
					{COPY.access.formManageDeniedLead}
					<br />
					{COPY.access.formManageDeniedTail}
				</p>
			</div>
		);
	}

	return (
		<div className={cn('flex h-full flex-col items-center justify-center', className)}>
			<h1 className="mb-2 text-xl font-semibold text-gray-900">Something went wrong</h1>
			<p className="text-center text-sm text-gray-600">
				{displayCountdownText && `Redirecting in ${countdown} seconds...`}
				{!displayCountdownText &&
					`We couldn't fetch the data. Please check your connection and try again later.`}
			</p>

			<Button
				className="mt-4"
				onClick={() => window.location.reload()}
			>
				Refresh page
			</Button>
		</div>
	);
}

export default ApiError;
